import {
  useCallback,
  useMemo,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  firstRunBasicInfoSchema,
  firstRunSpecificationTextSchema,
  isBlank,
} from '@/features/drbfm/schemas/firstRunInput.schema';
import { fieldErrorsOf } from '@/lib/forms/fieldErrors';
import { translate } from '@/lib/forms/messages';
import {
  ROLES_BY_MODE,
  type FirstRunInputSubmission,
  type FirstRunSpecifications,
  type SelectedSpecificationDocument,
  type SpecificationFieldValue,
  type SpecificationMode,
  type SpecificationRole,
  type SpecificationSourceKind,
} from './types';

export type BasicFieldKey = 'itemName' | 'title' | 'supplement';
export type FirstRunFieldKey = BasicFieldKey | SpecificationRole;
export type FirstRunErrors = Partial<Record<FirstRunFieldKey, string>>;

interface Draft {
  itemName: string;
  title: string;
  supplement: string;
  cadModelId?: string;
  mode: SpecificationMode;
  // Typed text survives a document selection, so removing the document gives it back.
  texts: Record<SpecificationRole, string>;
  documents: Partial<Record<SpecificationRole, SelectedSpecificationDocument>>;
  sourceKinds: Record<SpecificationRole, SpecificationSourceKind>;
  edited: Partial<
    Record<SpecificationRole, Record<string, Extract<SpecificationFieldValue, { kind: 'derived' }>>>
  >;
  activeEdited: Partial<Record<SpecificationRole, string>>;
}

const EMPTY: Draft = {
  itemName: '',
  title: '',
  supplement: '',
  mode: 'old_new',
  texts: { old: '', new: '', difference: '' },
  documents: {},
  sourceKinds: { old: 'text', new: 'text', difference: 'text' },
  edited: {},
  activeEdited: {},
};

const ALL_ROLES: readonly SpecificationRole[] = ['old', 'new', 'difference'];

const valueOf = (draft: Draft, role: SpecificationRole): SpecificationFieldValue => {
  const editedKey = draft.activeEdited[role];
  const edited = editedKey ? draft.edited[role]?.[editedKey] : undefined;
  if (draft.sourceKinds[role] === 'derived' && edited) return edited;
  const document = draft.sourceKinds[role] === 'document' ? draft.documents[role] : undefined;
  return document ? { kind: 'document', ...document } : { kind: 'text', text: draft.texts[role] };
};

const isFilled = (value: SpecificationFieldValue) =>
  value.kind === 'document' ? !isBlank(value.documentId) : !isBlank(value.text);

const without = (errors: FirstRunErrors, keys: readonly FirstRunFieldKey[]) => {
  if (!keys.some((key) => key in errors)) return errors;
  const next = { ...errors };
  keys.forEach((key) => delete next[key]);
  return next;
};

export type ValidationResult =
  | { ok: true; submission: FirstRunInputSubmission }
  | { ok: false; firstInvalid: FirstRunFieldKey };

// Hide the previous owner's state synchronously, including before any effects run.
const useOwnerState = <T,>(
  owner: string | undefined,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] => {
  const latestOwner = useRef(owner);
  useLayoutEffect(() => {
    latestOwner.current = owner;
  }, [owner]);
  const [stored, setStored] = useState({ owner, value: initial });
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (update) => {
      setStored((previous) => {
        if (latestOwner.current !== owner) return previous;
        const value = previous.owner === owner ? previous.value : initial;
        return {
          owner,
          value: typeof update === 'function' ? (update as (current: T) => T)(value) : update,
        };
      });
    },
    [owner, initial],
  );
  return [stored.owner === owner ? stored.value : initial, setValue];
};
const EMPTY_ERRORS: FirstRunErrors = {};

export const useFirstRunInputForm = (owner?: string) => {
  const [draft, setDraft] = useOwnerState<Draft>(owner, EMPTY);
  const [errors, setErrors] = useOwnerState<FirstRunErrors>(owner, EMPTY_ERRORS);

  const setBasic = useCallback(
    (key: BasicFieldKey, value: string) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => without(prev, [key]));
    },
    [setDraft, setErrors],
  );

  const setCadModelId = useCallback(
    (cadModelId: string | undefined) => {
      setDraft((prev) => ({ ...prev, cadModelId }));
    },
    [setDraft],
  );

  const setMode = useCallback(
    (mode: SpecificationMode) => {
      setDraft((prev) => ({ ...prev, mode }));
      setErrors((prev) => without(prev, ALL_ROLES));
    },
    [setDraft, setErrors],
  );

  const setText = useCallback(
    (role: SpecificationRole, text: string) => {
      setDraft((prev) => {
        const key = prev.activeEdited[role];
        const edited = key ? prev.edited[role]?.[key] : undefined;
        return prev.sourceKinds[role] === 'derived' && key && edited
          ? {
              ...prev,
              edited: {
                ...prev.edited,
                [role]: { ...prev.edited[role], [key]: { ...edited, text } },
              },
            }
          : { ...prev, texts: { ...prev.texts, [role]: text } };
      });
      setErrors((prev) => without(prev, [role]));
    },
    [setDraft, setErrors],
  );

  const setDocument = useCallback(
    (role: SpecificationRole, document: SelectedSpecificationDocument) => {
      // Copied field by field: a source object may carry more (extracted text), which must never be sent.
      const { documentId, versionId, title, fileName } = document;
      const reference = { documentId, versionId: versionId ?? null, title, ...(fileName === undefined ? {} : { fileName }) };
      setDraft((prev) => ({
        ...prev,
        documents: { ...prev.documents, [role]: reference },
        sourceKinds: { ...prev.sourceKinds, [role]: 'document' },
      }));
      setErrors((prev) => without(prev, [role]));
    },
    [setDraft, setErrors],
  );

  const clearDocument = useCallback(
    (role: SpecificationRole) => {
      setDraft((prev) => {
        const { [role]: _removed, ...documents } = prev.documents;
        return {
          ...prev,
          documents,
          sourceKinds: { ...prev.sourceKinds, [role]: 'text' },
        };
      });
    },
    [setDraft],
  );

  const changeDocument = useCallback(
    (role: SpecificationRole, document: SelectedSpecificationDocument | null) =>
      document ? setDocument(role, document) : clearDocument(role),
    [setDocument, clearDocument],
  );

  const switchSource = useCallback(
    (role: SpecificationRole, kind: SpecificationSourceKind) => {
      setDraft((prev) => {
        if (kind === 'document' && !prev.documents[role]) return prev;
        const key = prev.activeEdited[role];
        if (kind === 'derived' && (!key || !prev.edited[role]?.[key])) return prev;
        return { ...prev, sourceKinds: { ...prev.sourceKinds, [role]: kind } };
      });
      setErrors((prev) => without(prev, [role]));
    },
    [setDraft, setErrors],
  );

  const copyDocumentText = useCallback(
    (role: SpecificationRole, document: SelectedSpecificationDocument, text: string) => {
      setDraft((prev) => {
        const selected = prev.documents[role];
        if (
          !document.versionId ||
          selected?.documentId !== document.documentId ||
          selected.versionId !== document.versionId
        )
          return prev;
        const key = JSON.stringify([document.documentId, document.versionId]);
        const origin = {
          documentId: document.documentId,
          versionId: document.versionId,
          title: document.title,
          ...(document.fileName ? { fileName: document.fileName } : {}),
        };
        return {
          ...prev,
          sourceKinds: { ...prev.sourceKinds, [role]: 'derived' },
          activeEdited: { ...prev.activeEdited, [role]: key },
          edited: {
            ...prev.edited,
            [role]: {
              ...prev.edited[role],
              [key]: prev.edited[role]?.[key] ?? {
                kind: 'derived',
                text,
                origin,
              },
            },
          },
        };
      });
      setErrors((prev) => without(prev, [role]));
    },
    [setDraft, setErrors],
  );

  const values = useMemo(
    () => Object.fromEntries(ALL_ROLES.map((role) => [role, valueOf(draft, role)])) as Record<
      SpecificationRole,
      SpecificationFieldValue
    >,
    [draft],
  );

  const hiddenDraftKept = useMemo(() => {
    const hidden = draft.mode === 'old_new' ? 'difference' : 'old_new';
    return ROLES_BY_MODE[hidden].some((role) => isFilled(values[role]));
  }, [draft.mode, values]);

  const validate = useCallback((): ValidationResult => {
    const basic = firstRunBasicInfoSchema.safeParse({
      itemName: draft.itemName,
      title: draft.title,
      supplement: draft.supplement,
    });
    const found: FirstRunErrors = { ...fieldErrorsOf(basic) };
    const roles = ROLES_BY_MODE[draft.mode];
    roles.forEach((role) => {
      const value = values[role];
      if (!isFilled(value)) {
        found[role] = translate(`drbfm.firstRunInput.required.${role}`);
      } else if (value.kind !== 'document') {
        const text = firstRunSpecificationTextSchema.safeParse(value.text);
        if (!text.success) found[role] = text.error.issues[0].message;
      }
    });

    const order: FirstRunFieldKey[] = ['itemName', 'title', ...roles, 'supplement'];
    const firstInvalid = order.find((key) => found[key]);
    setErrors(found);
    if (firstInvalid || !basic.success) return { ok: false, firstInvalid: firstInvalid ?? 'itemName' };

    const specifications: FirstRunSpecifications =
      draft.mode === 'old_new'
        ? { mode: 'old_new', old: values.old, new: values.new }
        : { mode: 'difference', difference: values.difference };
    return {
      ok: true,
      submission: {
        itemName: basic.data.itemName,
        title: basic.data.title,
        ...(draft.cadModelId ? { cadModelId: draft.cadModelId } : {}),
        supplement: draft.supplement,
        specifications,
      },
    };
  }, [draft, values, setErrors]);

  const reset = useCallback(() => {
    setDraft(EMPTY);
    setErrors({});
  }, [setDraft, setErrors]);

  return {
    reset,
    itemName: draft.itemName,
    title: draft.title,
    supplement: draft.supplement,
    cadModelId: draft.cadModelId,
    mode: draft.mode,
    values,
    errors,
    hiddenDraftKept,
    sourceOptions: Object.fromEntries(
      ALL_ROLES.map((role) => [
        role,
        {
          document: !!draft.documents[role],
          derived: !!draft.activeEdited[role],
        },
      ]),
    ) as Record<SpecificationRole, { document: boolean; derived: boolean }>,
    switchSource,
    copyDocumentText,
    setBasic,
    setCadModelId,
    setMode,
    setText,
    changeDocument,
    validate,
  };
};

export type FirstRunInputForm = ReturnType<typeof useFirstRunInputForm>;
